import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { SignupData } from '../types/auth';
import { User } from '../types/user';
import { api } from '../services/api';
import { createSafeStorage } from '../utils/storage';

interface AuthState {
  user: User | null;
  users: User[];
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (user: User, token: string) => void;
  signup: (data: SignupData) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => Promise<void>;
  fetchUsers: () => Promise<void>;
}

const initialState: AuthState = {
  user: null,
  users: [],
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  login: () => {},
  signup: async () => {},
  logout: async () => {},
  updateUser: async () => {},
  fetchUsers: async () => {}
};

export const useAuthStore = create(
  persist<AuthState>(
    (set, get) => ({
      ...initialState,
      
      fetchUsers: async () => {
        set({ isLoading: true, error: null });
        try {
          // Fetch users from the real API
          const users = await api.admin.getUsers();
          set({ users });
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to fetch users' });
        } finally {
          set({ isLoading: false });
        }
      },
      
      login: (user, token) => {
        // Make sure user object has the required fields
        if (user && typeof user === 'object') {
          // Ensure role is set
          if (!user.role) {
            user.role = 'user';
          }
          
          api.updateToken(token);
          set({ 
            user, 
            token, 
            isAuthenticated: true,
            error: null,
            isLoading: false
          });
        } else {
          throw new Error('Invalid user data');
        }
      },

      signup: async (data) => {
        set({ isLoading: true, error: null });
        try {
          // Use the actual API instead of mock data
          const response = await api.auth.signup(data);
          
          // Update the store with the real user and token
          const { user, token } = response;
          
          // Make sure user has all fields
          const normalizedUser = {
            ...user,
            role: user.role || 'user',
            enneagramType: user.enneagramType || null,
            hasCompletedTest: user.hasCompletedTest || false
          };
          
          api.updateToken(token);
          set(state => ({ 
            user: normalizedUser,
            users: [...state.users, normalizedUser],
            token,
            isAuthenticated: true,
            error: null
          }));
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Signup failed';
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          // Call the actual logout API
          await api.auth.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null
          });
          api.updateToken(null);
        }
      },

      updateUser: async (userData) => {
        const currentUser = get().user;
        if (!currentUser) {
          throw new Error('No user logged in');
        }

        set({ isLoading: true, error: null });
        try {
          // Update the user via the API
          const updatedUser = await api.auth.updateUser(currentUser.id, userData);
          
          set(state => ({ 
            user: updatedUser, 
            users: state.users.map(u => 
              u.id === currentUser.id ? updatedUser : u
            ),
            error: null 
          }));
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Update failed';
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        users: state.users,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        isLoading: false,
        error: null
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          api.updateToken(state.token);
        }
      },
      storage: createSafeStorage(),
    }
  )
);